import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/layout/AppShell';
import HelpButton from './Guides/HelpButton';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Boxes, Search, AlertTriangle, LayoutDashboard, Bell, BarChart3, FileText
} from 'lucide-react';
import StockDetailView from './Inventory/StockDetailView';
import StockItemsList from './Inventory/StockItemsList';
import BatchLifecycleTab from './Inventory/BatchLifecycleTab';
import InventoryDashboard from './InventoryDashboard';
import CycleCounting from './CycleCounting';
import InventoryAnalytics from './InventoryAnalytics';
import ReplenishmentTab from './ReplenishmentTab';
import InventoryAlerts from './InventoryAlerts';
import SerialLifecycleView from './SerialLifecycleView';
import InventoryReports from './InventoryReports';

const TABS = [
    { id: 'dashboard', label: 'Analytics', icon: LayoutDashboard },
  { id: 'items', label: 'Stock Items' },
  { id: 'replenishment', label: 'Replenishment' },
  { id: 'expiring', label: 'Batch Lifecycle' },
  { id: 'cycle-counting', label: 'Cycle Counting' },
  { id: 'alerts', label: 'Alerts', icon: Bell },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'reports', label: 'Reports', icon: FileText },
];

export default function InventoryPage() {
  const [searchParams] = useSearchParams();
  const openItem = searchParams.get('item');
  const { systemSettings } = useAuth();

  const [selectedItem, setSelectedItem] = useState(openItem || null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showBelowMin, setShowBelowMin] = useState(false);

  const [overviewTab, setOverviewTab] = useState('dashboard');
  const [expiringData, setExpiringData] = useState(null);
  const [alertCount, setAlertCount] = useState(0);
  const [batchStatusFilter, setBatchStatusFilter] = useState('expiring');
  const [serialLifecycleItem, setSerialLifecycleItem] = useState(null);
  const fmtNum = (v) => (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadExpiring = useCallback(async () => {
    try {
      await api.post('/api/inventory/batches/auto-expire').catch(() => {});
      const r = await api.get(`/api/inventory/batches/expiring-soon?days=60&status_filter=${batchStatusFilter}`);
      setExpiringData(r.data);
    } catch {}
  }, [batchStatusFilter]);

  const loadAlertCount = useCallback(async () => {
    try {
      const r = await api.get('/api/inventory/alerts');
      setAlertCount(r.data?.summary?.total || 0);
    } catch {}
  }, []);

  useEffect(() => {
    if (overviewTab === 'expiring') loadExpiring();
  }, [overviewTab, loadExpiring]);

  useEffect(() => { loadAlertCount(); }, [loadAlertCount]);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (showBelowMin) params.set('below_minimum', 'true');
      const r = await api.get(`/api/inventory/overview?${params}`);
      setItems(r.data.data || []);
    } catch {}
    setLoading(false);
  }, [debouncedSearch, showBelowMin]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  if (selectedItem) {
    return <StockDetailView itemCode={selectedItem} openItem={openItem} onBack={() => setSelectedItem(null)} />;
  }

  const tabBadges = { expiring: expiringData?.total, alerts: alertCount };

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              <Boxes className="inline h-5 w-5 mr-2 text-primary" />Inventory Control
            </h1>
            <HelpButton guideId="inventory" label="Inventory Control Guide" />
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{items.length} inventoried item(s){debouncedSearch ? ` matching "${debouncedSearch}"` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/Eyvo_Inventory_Control_User_Guide.pdf" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border bg-background hover:bg-accent transition-colors"
            data-testid="inventory-user-guide-btn">
            <FileText className="h-3.5 w-3.5" /> User Guide
          </a>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" checked={showBelowMin} onChange={e => setShowBelowMin(e.target.checked)} className="h-3.5 w-3.5 rounded" />
            <AlertTriangle className="h-3 w-3 text-amber-500" /> Below minimum only
          </label>
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={e => { setSearch(e.target.value); if (e.target.value && overviewTab !== 'items') setOverviewTab('items'); }} placeholder="Search items..." className="pl-8 h-8 text-sm" data-testid="inventory-search" />
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b mb-4">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setOverviewTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${overviewTab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.icon && <t.icon className="h-3.5 w-3.5 inline mr-1" />}
            {t.label}
            {tabBadges[t.id] > 0 && <span className="ml-1.5 text-[10px] bg-red-100 text-red-700 rounded-full px-1.5 py-0.5">{tabBadges[t.id]}</span>}
          </button>
        ))}
      </div>

      {overviewTab === 'dashboard' && <InventoryDashboard onNavigate={(tab) => setOverviewTab(tab)} onItemClick={(itemCode) => setSelectedItem(itemCode)} />}
      {overviewTab === 'items' && <StockItemsList items={items} loading={loading} onItemClick={(itemCode) => setSelectedItem(itemCode)} />}
      {overviewTab === 'replenishment' && <ReplenishmentTab onItemClick={(itemCode) => setSelectedItem(itemCode)} />}
      {overviewTab === 'expiring' && (serialLifecycleItem
        ? <SerialLifecycleView itemCode={serialLifecycleItem} onBack={() => setSerialLifecycleItem(null)} />
        : <BatchLifecycleTab expiringData={expiringData} batchStatusFilter={batchStatusFilter} setBatchStatusFilter={setBatchStatusFilter}
            loadExpiring={loadExpiring} onItemClick={(itemCode) => setSelectedItem(itemCode)}
            onSerialDrillDown={(itemCode) => setSerialLifecycleItem(itemCode)} fmtNum={fmtNum} />
      )}
      {overviewTab === 'cycle-counting' && <CycleCounting setSelectedItem={setSelectedItem} />}
      {overviewTab === 'alerts' && <InventoryAlerts onItemClick={(itemCode) => setSelectedItem(itemCode)} />}
      {overviewTab === 'analytics' && <InventoryAnalytics onItemClick={(itemCode) => setSelectedItem(itemCode)} />}
      {overviewTab === 'reports' && <InventoryReports />}
    </AppShell>
  );
}
